/**
 * Composant Lobby - Création et rejoindre une room
 */

import React, { useState, useCallback } from 'react';
import { useGameStore } from '../store/gameStore';
import { socketService } from '../services/socketService';
import { Tutorial } from './Tutorial';


type TimerOption = 30 | 60 | 90 | null;
type AIDifficulty = 'easy' | 'medium' | 'hard';

export const Lobby: React.FC = () => {
  const { connected, playerName, setPlayerName, roomId, view, isVsAI } = useGameStore();
  const error = useGameStore((state) => state.error);
  const matchmakingActive = useGameStore((state) => state.matchmakingActive);

  const urlJoinCode = new URLSearchParams(window.location.search).get('join') ?? '';
  const [joinCode, setJoinCode] = useState(urlJoinCode.toUpperCase());
  const [timerOption, setTimerOption] = useState<TimerOption>(60);
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('medium');
  const [mode, setMode] = useState<'menu' | 'create' | 'join' | 'matchmaking' | 'vsai'>(
    urlJoinCode ? 'join' : 'menu'
  );
  const [showTutorial, setShowTutorial] = useState(false);

  const handleJoinMatchmaking = () => {
    if (!playerName.trim()) return;
    useGameStore.setState({ matchmakingActive: true });
    socketService.joinMatchmaking(playerName.trim());
    setMode('matchmaking');
  };

  const [copied, setCopied] = useState(false);
  const handleCopyLink = useCallback(() => {
    const url = `${window.location.origin}?join=${roomId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [roomId]);

  const handleLeaveMatchmaking = () => {
    socketService.leaveMatchmaking();
    useGameStore.setState({ matchmakingActive: false });
    setMode('menu');
  };

  const handleCreateRoom = () => {
    if (!playerName.trim()) return;
    socketService.createRoom(playerName.trim(), timerOption);
  };

  const handlePlayVsAI = () => {
    if (!playerName.trim()) return;
    socketService.createRoomVsAI(playerName.trim(), timerOption, aiDifficulty);
  };

  const handleJoinRoom = () => {
    if (!playerName.trim() || !joinCode.trim()) return;
    socketService.joinRoom(joinCode.trim().toUpperCase(), playerName.trim());
  };

  // Vue matchmaking
  if (mode === 'matchmaking' || (matchmakingActive && view === 'lobby')) {
    return (
      <div className="card max-w-md mx-auto text-center space-y-6">
        <h2 className="text-2xl font-bold text-white">Recherche d'un adversaire</h2>

        <div className="flex items-center justify-center gap-2 py-4">
          <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" />
          <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
          <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
        </div>

        <p className="text-gray-400">En attente d'un autre joueur...</p>

        <button
          onClick={handleLeaveMatchmaking}
          className="btn btn-secondary w-full"
        >
          Annuler
        </button>
      </div>
    );
  }

  // Vue d'attente
  if (view === 'waiting' && roomId) {
    // Si vs IA, affichage simplifié (la partie va démarrer dans un instant)
    if (isVsAI) {
      return (
        <div className="card max-w-md mx-auto text-center space-y-6">
          <h2 className="text-2xl font-bold text-white">Préparation...</h2>
          <p className="text-purple-300">L'IA se prépare au combat 🤖</p>
          <div className="flex items-center justify-center gap-2">
            <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce" />
            <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
            <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
          </div>
        </div>
      );
    }

    return (
      <div className="card max-w-md mx-auto text-center space-y-6">
        <h2 className="text-2xl font-bold text-white">En attente...</h2>

        <div className="bg-gray-900 rounded-lg p-6">
          <p className="text-gray-400 text-sm mb-2">Code de la room</p>
          <p className="text-4xl font-mono font-bold text-blue-400 tracking-wider">
            {roomId}
          </p>
        </div>

        <p className="text-gray-400">
          Partagez ce code avec votre adversaire
        </p>

        <button
          onClick={handleCopyLink}
          className="btn btn-secondary w-full"
        >
          {copied ? '✓ Lien copié !' : 'Copier le lien d\'invitation'}
        </button>

        <div className="flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" />
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
        </div>

        <button
          onClick={() => {
            socketService.leaveRoom();
            setMode('menu');
          }}
          className="btn btn-secondary w-full"
        >
          Annuler
        </button>
      </div>
    );
  }

  // Menu principal
  if (mode === 'menu') {
    return (
      <>
        {showTutorial && <Tutorial onClose={() => setShowTutorial(false)} />}
      <div className="card max-w-md mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-2">PathBlock</h1>
          <p className="text-gray-400">Jeu de stratégie 1v1</p>
        </div>

        {!connected && (
          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg px-4 py-2 text-yellow-400 text-sm text-center">
            Connexion au serveur...
          </div>
        )}

        <div className="space-y-3">
          <label className="block">
            <span className="text-gray-400 text-sm">Votre nom</span>
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Entrez votre nom"
              className="input w-full mt-1"
              maxLength={20}
            />
          </label>
        </div>

        <button
          onClick={handleJoinMatchmaking}
          disabled={!connected || !playerName.trim()}
          className="btn btn-primary w-full"
        >
          Jouer en ligne
        </button>

        <button
          onClick={() => setMode('vsai')}
          disabled={!connected || !playerName.trim()}
          className="btn btn-secondary w-full border-purple-600 text-purple-300 hover:bg-purple-600/20"
        >
          Jouer contre l'IA
        </button>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setMode('create')}
            disabled={!connected || !playerName.trim()}
            className="btn btn-secondary"
          >
            Créer
          </button>
          <button
            onClick={() => setMode('join')}
            disabled={!connected || !playerName.trim()}
            className="btn btn-secondary"
          >
            Rejoindre
          </button>
        </div>

        <button
          onClick={() => setShowTutorial(true)}
          className="w-full py-2 text-sm text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded-lg transition-colors"
        >
          ? Comment jouer
        </button>
      </div>
      </>
    );
  }

  // Création de room
  if (mode === 'create') {
    return (
      <div className="card max-w-md mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setMode('menu')}
            className="text-gray-400 hover:text-white"
          >
            ← Retour
          </button>
          <h2 className="text-xl font-bold text-white">Créer une partie</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-sm block mb-2">
              Temps par tour
            </label>
            <div className="grid grid-cols-4 gap-2">
              {([30, 60, 90, null] as TimerOption[]).map((option) => (
                <button
                  key={option ?? 'unlimited'}
                  onClick={() => setTimerOption(option)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    timerOption === option
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {option ? `${option}s` : '∞'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-4 py-2 text-red-400 text-sm">
            {error}
          </div>
        )}

        <button
          onClick={handleCreateRoom}
          disabled={!connected}
          className="btn btn-primary w-full"
        >
          Créer la partie
        </button>
      </div>
    );
  }

  // Mode vs IA
  if (mode === 'vsai') {
    return (
      <div className="card max-w-md mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setMode('menu')}
            className="text-gray-400 hover:text-white"
          >
            ← Retour
          </button>
          <h2 className="text-xl font-bold text-white">Jouer contre l'IA</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-sm block mb-2">
              Difficulté de l'IA
            </label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { value: 'easy', label: 'Facile', desc: 'Se déplace sans bloquer' },
                { value: 'medium', label: 'Moyen', desc: 'Stratégie BFS + blocage' },
                { value: 'hard', label: 'Difficile', desc: 'Blocage agressif + anticipation' },
              ] as { value: AIDifficulty; label: string; desc: string }[]).map(({ value, label, desc }) => (
                <button
                  key={value}
                  onClick={() => setAiDifficulty(value)}
                  title={desc}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    aiDifficulty === value
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-gray-600 text-xs mt-1 text-center">
              {aiDifficulty === 'easy' && 'Avance vers le but sans stratégie de blocage'}
              {aiDifficulty === 'medium' && 'Bloque selon la distance, réserve des murs'}
              {aiDifficulty === 'hard' && 'Blocage agressif, anticipe 2 coups, exploite le terrain'}
            </p>
          </div>

          <div>
            <label className="text-gray-400 text-sm block mb-2">
              Temps par tour
            </label>
            <div className="grid grid-cols-4 gap-2">
              {([30, 60, 90, null] as TimerOption[]).map((option) => (
                <button
                  key={option ?? 'unlimited'}
                  onClick={() => setTimerOption(option)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    timerOption === option
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {option ? `${option}s` : '∞'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-4 py-2 text-red-400 text-sm">
            {error}
          </div>
        )}

        <button
          onClick={handlePlayVsAI}
          disabled={!connected}
          className="btn w-full bg-purple-600 hover:bg-purple-700 text-white"
        >
          Lancer la partie vs IA
        </button>
      </div>
    );
  }

  // Rejoindre une room
  return (
    <div className="card max-w-md mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => setMode('menu')}
          className="text-gray-400 hover:text-white"
        >
          ← Retour
        </button>
        <h2 className="text-xl font-bold text-white">Rejoindre une partie</h2>
      </div>

      <div className="space-y-3">
        {/* Champ nom affiché ici aussi si vide (cas lien direct ?join=CODE) */}
        {!playerName.trim() && (
          <label className="block">
            <span className="text-gray-400 text-sm">Votre nom</span>
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Entrez votre nom"
              className="input w-full mt-1"
              maxLength={20}
              autoFocus
            />
          </label>
        )}
        <label className="block">
          <span className="text-gray-400 text-sm">Code de la room</span>
          <input
            type="text"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            placeholder="XXXXXX"
            className="input w-full mt-1 text-center text-2xl font-mono tracking-wider"
            maxLength={6}
          />
        </label>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-4 py-2 text-red-400 text-sm">
          {error}
        </div>
      )}

      <button
        onClick={handleJoinRoom}
        disabled={!connected || joinCode.length < 6 || !playerName.trim()}
        className="btn btn-primary w-full"
      >
        Rejoindre
      </button>
    </div>
  );
};
